import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './index';
import { adminApi } from '../services/api';
import type { InvitationDetails } from '../types';
import type { ProviderType } from './ProviderInvitationResult';

/**
 * Besiktningsmän or Jurister that admin invited and who have not registered
 * yet (ClickUp 869fjt7ma), with the mail status and a way to send the
 * invitation again and copy the new link. Shared by both expert lists.
 */
export function ProviderInvitationsPanel({ type }: { type: ProviderType }) {
  const { t, i18n } = useTranslation();
  const [invitations, setInvitations] = useState<InvitationDetails[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [links, setLinks] = useState<Record<string, string>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    adminApi.getProviderInvitations(type)
      .then(list => { if (!cancelled) setInvitations(list); })
      .catch(() => { if (!cancelled) setError(t('providerInvite.loadError')); });
    return () => { cancelled = true; };
  }, [type, t]);

  const resend = async (invitation: InvitationDetails) => {
    setBusyId(invitation.id); setError('');
    try {
      const updated = await adminApi.resendProviderInvitation(invitation.id);
      setInvitations(current => current.map(item => (item.id === updated.id ? { ...item, ...updated } : item)));
      if (updated.registrationUrl) setLinks(current => ({ ...current, [updated.id]: updated.registrationUrl! }));
    } catch { setError(t('providerInvite.resendError')); }
    finally { setBusyId(null); }
  };

  const copy = (id: string) => {
    void navigator.clipboard?.writeText(links[id]).then(() => setCopiedId(id), () => setCopiedId(null));
  };

  const date = (value?: string | null) => value ? new Intl.DateTimeFormat(i18n.language).format(new Date(value)) : '';

  if (invitations.length === 0 && !error) return null;
  return <Card>
    <div className="card-body" data-testid="provider-invitations">
      <h2 className="h6 fw-bold mb-1">{t(`providerInvite.list.title.${type}`)}</h2>
      <p className="text-muted small">{t(`providerInvite.list.description.${type}`)}</p>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      <ul className="list-group">
        {invitations.map(invitation => <li key={invitation.id} className="list-group-item d-flex flex-column flex-md-row gap-2 align-items-md-center">
          <div className="flex-grow-1 text-break">
            <div className="fw-semibold">{invitation.companyName || invitation.customerName || invitation.contactEmail}</div>
            <div className="small text-muted">{invitation.contactEmail}</div>
            <div className="small">
              {t('companySetup.inviteLink.delivery', { status: t(`companySetup.delivery.${invitation.emailStatus || 'not_sent'}`) })}
              {invitation.linkState === 'expired'
                ? <span className="badge bg-warning text-dark ms-2">{t('providerInvite.list.expired')}</span>
                : invitation.expiresAt && <span className="text-muted ms-2">{t('companySetup.expires', { date: date(invitation.expiresAt) })}</span>}
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row gap-2">
            <button type="button" className="btn btn-sm btn-outline-primary provider-invitation-action" disabled={busyId === invitation.id} onClick={() => void resend(invitation)}>
              <i className="feather-send me-1" aria-hidden="true"></i>
              {busyId === invitation.id ? t('providerInvite.resending') : t('providerInvite.resend')}
            </button>
            {links[invitation.id] && <button type="button" className="btn btn-sm btn-link provider-invitation-action" onClick={() => copy(invitation.id)}>
              {copiedId === invitation.id ? t('companySetup.inviteLink.copied') : t('providerInvite.list.copyNewLink')}
            </button>}
          </div>
        </li>)}
      </ul>
    </div>
  </Card>;
}
